import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import "./Header.css";

export default function Header() {
  const [user, setUser] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navigate = useNavigate();

  const loadGoogleUser = () => {
    const savedUser = localStorage.getItem("currentUser");
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    if (savedUser && isLoggedIn) {
      try {
        setGoogleUser(JSON.parse(savedUser));
      } catch (error) {
        console.error("Failed to parse Google user:", error);
        localStorage.removeItem("currentUser");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("loginProvider");
        setGoogleUser(null);
      }
    } else {
      setGoogleUser(null);
    }
  };

  useEffect(() => {
    loadGoogleUser();

    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const adminRef = doc(db, "admins", currentUser.email);
          const adminSnap = await getDoc(adminRef);
          setIsAdmin(adminSnap.exists() && adminSnap.data().role === "admin");
        } catch (error) {
          console.error("Admin check failed:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const handleAuthChange = () => {
      loadGoogleUser();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("auth-change", handleAuthChange);

    return () => {
      unsubscribe();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, []);

  const handleLogout = async () => {
    try {
      const auth = getAuth();

      if (auth.currentUser) {
        await signOut(auth);
      }

      localStorage.removeItem("currentUser");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("loginProvider");

      setUser(null);
      setGoogleUser(null);
      setIsAdmin(false);
      setProfileOpen(false);
      setIsNavCollapsed(true);

      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const loggedInUser = user || googleUser;

  const displayName =
    user?.displayName ||
    googleUser?.name ||
    googleUser?.email ||
    "User";

  const displayEmail =
    user?.email ||
    googleUser?.email ||
    "";

  const getInitials = (nameOrEmail) => {
    if (!nameOrEmail) return "U";

    const value = nameOrEmail.trim();

    if (value.includes("@")) {
      return value.substring(0, 2).toUpperCase();
    }

    const parts = value.split(" ").filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return value.substring(0, 2).toUpperCase();
  };

  return (
    <nav
      className={`navbar navbar-expand-lg fixed-top ${
        scrolled ? "navbar-light bg-white shadow-sm" : "navbar-dark"
      }`}
      style={{
        backgroundColor: scrolled ? "#ffffff" : "#1f2937",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      <div className="container-fluid">
        <Link
          className={`navbar-brand d-flex align-items-center ${
            scrolled ? "navbar-brand-orange" : "text-white"
          }`}
          to="/"
          style={{ padding: 0 }}
        >
          <img
            src={scrolled ? "/assets/newlogo.png" : "/assets/newlogowhite.png"}
            alt="SS Logo"
            style={{ height: 90, width: "auto", marginRight: 12 }}
          />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setIsNavCollapsed(!isNavCollapsed)}
          aria-controls="navbarNav"
          aria-expanded={!isNavCollapsed}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          className={`collapse navbar-collapse ${isNavCollapsed ? "" : "show"}`}
          id="navbarNav"
        >
          <ul className="navbar-nav ms-auto align-items-lg-center">
            <li className="nav-item">
              <Link
                className={`nav-link ms-2 ${
                  scrolled ? "nav-link-orange" : "text-white"
                }`}
                to="/"
                onClick={() => setIsNavCollapsed(true)}
              >
                Home
              </Link>
            </li>

            <li className="nav-item">
              <Link
                className={`nav-link ms-2 ${
                  scrolled ? "nav-link-orange" : "text-white"
                }`}
                to="/aboutus"
                onClick={() => setIsNavCollapsed(true)}
              >
                About Us
              </Link>
            </li>

            <li className="nav-item">
              <Link
                className={`nav-link ms-2 ${
                  scrolled ? "nav-link-orange" : "text-white"
                }`}
                to="/contactus"
                onClick={() => setIsNavCollapsed(true)}
              >
                Contact
              </Link>
            </li>

            <li className="nav-item">
              <Link
                className={`nav-link ms-2 ${
                  scrolled ? "nav-link-orange" : "text-white"
                }`}
                to="/inventory"
                onClick={() => setIsNavCollapsed(true)}
              >
                Auction
              </Link>
            </li>

            {isAdmin && (
              <li className="nav-item">
                <Link
                  className={`nav-link ms-2 ${
                    scrolled
                      ? "nav-link-orange fw-bold"
                      : "text-warning fw-bold"
                  }`}
                  to="/admin"
                  onClick={() => setIsNavCollapsed(true)}
                >
                  Admin Panel
                </Link>
              </li>
            )}

            {loggedInUser ? (
              <li className="nav-item profile-menu-wrapper ms-lg-3 mt-2 mt-lg-0">
                <button
                  type="button"
                  className={`profile-trigger ${
                    scrolled ? "profile-light" : "profile-dark"
                  }`}
                  onClick={() => setProfileOpen((prev) => !prev)}
                >
                  <div className="profile-avatar">
                    <span>{getInitials(displayName)}</span>
                  </div>

                  <div className="profile-text d-none d-lg-block">
                    <span className="profile-name">{displayName}</span>
                    <small>My Account</small>
                  </div>

                  <i className="bi bi-chevron-down profile-chevron"></i>
                </button>

                {profileOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-header">
                      <div className="profile-avatar large">
                        <span>{getInitials(displayName)}</span>
                      </div>

                      <div className="profile-dropdown-user">
                        <strong>{displayName}</strong>
                        {displayEmail && <small>{displayEmail}</small>}
                      </div>
                    </div>

                    <div className="profile-dropdown-divider"></div>

                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="profile-dropdown-item"
                        onClick={() => {
                          setProfileOpen(false);
                          setIsNavCollapsed(true);
                        }}
                      >
                        <i className="bi bi-speedometer2"></i>
                        Admin Panel
                      </Link>
                    )}

                    <Link
                      to="/inventory"
                      className="profile-dropdown-item"
                      onClick={() => {
                        setProfileOpen(false);
                        setIsNavCollapsed(true);
                      }}
                    >
                      <i className="bi bi-car-front"></i>
                      Browse Inventory
                    </Link>

                    <Link
                      to="/contactus"
                      className="profile-dropdown-item"
                      onClick={() => {
                        setProfileOpen(false);
                        setIsNavCollapsed(true);
                      }}
                    >
                      <i className="bi bi-headset"></i>
                      Contact Support
                    </Link>

                    <div className="profile-dropdown-divider"></div>

                    <button
                      type="button"
                      className="profile-dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right"></i>
                      Logout
                    </button>
                  </div>
                )}
              </li>
            ) : (
              <li className="nav-item ms-lg-3 mt-2 mt-lg-0">
                <Link
                  to="/login"
                  className={`modern-login-btn ${
                    scrolled ? "modern-login-light" : "modern-login-dark"
                  }`}
                  onClick={() => setIsNavCollapsed(true)}
                >
                  <span className="login-icon-circle">
                    <i className="bi bi-person-fill"></i>
                  </span>
                  <span>Login</span>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}